import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, ArrowRight, MessageCircle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

function useCountdown() {
  const target = new Date();
  target.setHours(23, 59, 59, 0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { h, m, s };
}

const WA = "https://wa.me/919819933676?text=Namaste!%20Mujhe%20Divya%20Dhara%20ka%201%20din%20ka%20%E2%82%B984%20trial%20book%20karna%20hai.%20Please%20contact%20karein.%20%F0%9F%A5%9B";

export default function LimitedTimeOffer() {
  const { t } = useI18n();
  const { h, m, s } = useCountdown();

  return (
    <section className="dd-section bg-dd-page relative overflow-hidden" data-testid="limited-offer">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(ellipse, #C9A24C 0%, transparent 70%)" }} />
      <div className="dd-container relative text-center max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="inline-flex items-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase shadow-lg"
        >
          <Zap className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} /> {t.offer.eyebrow}
        </motion.div>

        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 dd-shimmer">{t.offer.title}</h2>
        <p className="mt-4 text-dd-cream-muted max-w-xl mx-auto">{t.offer.subtitle}</p>

        <div className="mt-8 inline-flex gap-3 md:gap-4">
          {[{ v: h, l: "Hours" }, { v: m, l: "Minutes" }, { v: s, l: "Seconds" }].map((x, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white border border-dd-gold/40 rounded-xl px-5 md:px-7 py-4 shadow-lg"
            >
              <div className="font-serif text-4xl md:text-5xl text-[#8F6E28] tabular-nums font-bold">{String(x.v).padStart(2, "0")}</div>
              <div className="dd-label text-dd-ink-muted text-[0.6rem] mt-1">{x.l}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 inline-flex items-center gap-2 bg-dd-gold/15 border border-dd-gold/40 rounded-full px-4 py-2 text-sm text-dd-gold-light font-medium">
          <Zap className="w-4 h-4" fill="currentColor" strokeWidth={0} /> {t.offer.remaining}
        </div>

        <div className="mt-8">
          <a
            href={WA}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] hover:from-dd-gold-light hover:to-dd-gold text-dd-ink-dark px-8 py-4 rounded-full font-bold text-base shadow-[0_12px_28px_-8px_rgba(201,162,76,0.55)] transition-all tracking-wide"
            data-testid="offer-cta"
          >
            <MessageCircle className="w-4 h-4" />
            {t.offer.cta} <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-4 text-xs text-dd-cream-muted italic">{t.offer.fine}</p>
        </div>
      </div>
    </section>
  );
}
